import { useState } from 'react';

import type {
  CashAccountChoice,
  ExpenseHead,
  RecurringExpense,
  SaveRecurringExpenseInput,
} from '@/api/cash';
import type { PaymentMethod, RecurrenceFrequency } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass, selectClass } from '@/components/Field';
import { HeadField } from './HeadField';
import { expenseMethods } from '@core/cash/expense';
import { accountsFor, defaultPaidFrom, paidFromAfter, paidFromLabel } from '@core/cash/paidFrom';
import {
  draftOf,
  emptyRecurring,
  frequencyLabels,
  monthNames,
  recurringProblem,
  recurringRequest,
  scheduleChanged,
  scheduleWords,
  weekdayNames,
  type RecurringDraft,
} from '@core/cash/recurring';
import { formatDate, toIsoDate } from '@core/format/dates';
import { paymentMethodLabel } from '@core/bookings/paymentWords';

const frequencies: RecurrenceFrequency[] = ['Monthly', 'Weekly', 'Yearly'];

/**
 * A recurring expense, new or edited (Cash &amp; bank step 8) — what it is, how much it usually is,
 * how it is paid, and when it falls due. **A page, not a drawer** (2026-09-08), reached from the
 * Expenses register. It never posts: the clerk records each occurrence from the due list.
 */
export function RecurringExpensePage({
  template,
  heads,
  accounts,
  busy = false,
  mayAddHead = true,
  onCancel,
  onSave,
  onUndoSkip,
}: {
  template: RecurringExpense | undefined;
  heads: ExpenseHead[];
  accounts: CashAccountChoice[] | undefined;
  busy?: boolean;
  /** Whether the head picker may offer to add one (`Expense:Create`). */
  mayAddHead?: boolean;
  onCancel: () => void;
  onSave: (input: SaveRecurringExpenseInput) => void;
  /** Takes a skip back; absent when this person may not (`Expense:Create`). */
  onUndoSkip?: ((skipId: number) => void) | undefined;
}) {
  const firstField = useAutoFocusFirstField();
  const [today] = useState(() => toIsoDate(new Date()) ?? '');
  const [draft, setDraft] = useState<RecurringDraft>(() =>
    template ? draftOf(template) : emptyRecurring(today),
  );
  const change = (next: Partial<RecurringDraft>) => setDraft((was) => ({ ...was, ...next }));

  const problem = recurringProblem(draft, heads);
  const choices = accountsFor(draft.method, accounts);
  const shown = draft.cashAccountId ?? defaultPaidFrom(draft.method, accounts);
  const schedule = scheduleWords({
    frequency: draft.frequency,
    dayOfMonth: draft.frequency === 'Weekly' ? null : Number(draft.dayOfMonth) || null,
    dayOfWeek: draft.frequency === 'Weekly' ? draft.dayOfWeek : null,
    monthOfYear: draft.frequency === 'Yearly' ? draft.monthOfYear : null,
  });

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope}>
      <PageHeading
        title={template ? `Edit ${template.name}` : 'New recurring expense'}
        subtitle="Rent, electricity, a licence — it falls due and the clerk records it"
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              data-enter-key-commit
              shortcut="save"
              disabled={problem !== null || busy}
              onClick={() => {
                const request = recurringRequest(draft, heads);

                if (request) {
                  onSave(request);
                }
              }}
            >
              Save
            </Button>
          </>
        }
      />

      <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">What it is</p>

          <div className="flex flex-col gap-3">
            <Field
              label="Name"
              required
              hint="What the shop calls it — “Shop rent”, “Electricity”."
            >
              <input
                ref={firstField}
                type="text"
                disabled={busy}
                value={draft.name}
                onChange={(event) => change({ name: event.target.value })}
                className={controlClass}
              />
            </Field>

            <HeadField
              heads={heads}
              value={draft.headId}
              onChange={(headId) => change({ headId })}
              disabled={busy}
              mayAdd={mayAddHead}
            />

            <Field
              label="Typical amount"
              required
              hint="Offered on the voucher, and changeable there."
            >
              <input
                type="number"
                min={0}
                step="0.01"
                disabled={busy}
                value={draft.amount}
                onChange={(event) => change({ amount: event.target.value })}
                onWheel={(event) => event.currentTarget.blur()}
                className={controlClass}
              />
            </Field>

            <Field label="How it is paid">
              <select
                disabled={busy}
                value={draft.method}
                onChange={(event) => {
                  const method = event.target.value as PaymentMethod;

                  change({
                    method,
                    cashAccountId:
                      draft.cashAccountId === null
                        ? null
                        : paidFromAfter(draft.cashAccountId, method, accounts),
                  });
                }}
                className={selectClass}
              >
                {expenseMethods.map((method) => (
                  <option key={method} value={method}>
                    {paymentMethodLabel[method]}
                  </option>
                ))}
              </select>
            </Field>

            {choices.length > 0 && (
              <Field label="Paid from">
                <select
                  disabled={busy}
                  value={shown ?? ''}
                  onChange={(event) => change({ cashAccountId: Number(event.target.value) })}
                  className={selectClass}
                >
                  {shown === null && <option value="">The branch’s usual account</option>}
                  {choices.map((account) => (
                    <option key={account.id} value={account.id}>
                      {paidFromLabel(account)}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label="Notes"
              hint="Copied onto each voucher — the landlord, the account number."
            >
              <input
                type="text"
                disabled={busy}
                value={draft.notes}
                onChange={(event) => change({ notes: event.target.value })}
                className={controlClass}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <p className="mb-2.5 text-sm font-semibold text-gray-900">When it falls due</p>

          <div className="flex flex-col gap-3">
            <div role="group" aria-label="How often" className="grid grid-cols-3 gap-2">
              {frequencies.map((frequency) =>
                frequency === draft.frequency ? (
                  <div
                    key={frequency}
                    className="border-primary bg-primary-transparent rounded-md border-2 p-2.5 text-center text-[length:var(--scale-list-name)] font-semibold text-gray-900"
                  >
                    {frequencyLabels[frequency]}
                  </div>
                ) : (
                  <button
                    key={frequency}
                    type="button"
                    disabled={busy}
                    onClick={() => change({ frequency })}
                    className="border-border-color hover:bg-light cursor-pointer rounded-md border bg-white p-2.5 text-[length:var(--scale-list-name)] text-gray-900"
                  >
                    {frequencyLabels[frequency]}
                  </button>
                ),
              )}
            </div>

            {draft.frequency === 'Weekly' ? (
              <Field label="Day of the week" required>
                <select
                  disabled={busy}
                  value={draft.dayOfWeek ?? ''}
                  onChange={(event) => change({ dayOfWeek: Number(event.target.value) })}
                  className={selectClass}
                >
                  {draft.dayOfWeek === null && <option value="">Choose a day</option>}
                  {weekdayNames.map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Day" required hint="1 to 31; a short month uses its last day.">
                  <input
                    type="number"
                    min={1}
                    max={31}
                    disabled={busy}
                    value={draft.dayOfMonth}
                    onChange={(event) => change({ dayOfMonth: event.target.value })}
                    onWheel={(event) => event.currentTarget.blur()}
                    className={controlClass}
                  />
                </Field>
                {draft.frequency === 'Yearly' && (
                  <Field label="Month" required>
                    <select
                      disabled={busy}
                      value={draft.monthOfYear ?? ''}
                      onChange={(event) => change({ monthOfYear: Number(event.target.value) })}
                      className={selectClass}
                    >
                      {draft.monthOfYear === null && <option value="">Choose a month</option>}
                      {monthNames.map((name, index) => (
                        <option key={name} value={index + 1}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}
              </div>
            )}

            <Field label="Starts on" required hint="Nothing before this day is ever due.">
              <input
                type="date"
                disabled={busy}
                value={draft.startsOn}
                onChange={(event) => change({ startsOn: event.target.value })}
                className={controlClass}
              />
            </Field>

            {template && (
              <label className="flex cursor-pointer items-center gap-2 text-[length:var(--scale-list-name)] text-gray-900">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  disabled={busy}
                  onChange={(event) => change({ isActive: event.target.checked })}
                />
                In use — untick to stop it falling due
              </label>
            )}
          </div>

          <Notice tone="info" title={schedule}>
            {template && scheduleChanged(draft, template)
              ? 'The next due day is worked out again when you save.'
              : template?.nextDueOn && template.isActive
                ? `Next due ${formatDate(template.nextDueOn)}.`
                : 'It is listed as due from a week before each day, and the clerk records it.'}
          </Notice>
        </Card>
      </div>

      {template && template.skips.length > 0 && (
        <Card>
          <p className="mb-1 text-sm font-semibold text-gray-900">Skipped</p>
          <p className="text-default mb-2.5 text-[length:var(--scale-list-meta)]">
            Paid some other way, so not recorded here. Undo one and it is due again.
          </p>
          <ul className="ps-0">
            {template.skips.map((skip) => (
              <li
                key={skip.id}
                className="border-border-color flex list-none items-center justify-between gap-2 border-t py-2 first:border-t-0"
              >
                <span className="flex flex-col">
                  <span className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                    Due {formatDate(skip.dueOn)}
                  </span>
                  <span className="text-default text-[length:var(--scale-list-meta)]">
                    Skipped on {formatDate(skip.skippedOn)}
                  </span>
                </span>
                {onUndoSkip && (
                  <Button disabled={busy} onClick={() => onUndoSkip(skip.id)}>
                    Undo
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {problem && <p className="text-warning text-[length:var(--scale-list-meta)]">{problem}</p>}
    </div>
  );
}
